/**
 * Currency utility functions for displaying prices with correct symbols
 */

// Map of currency codes to their symbols
const CURRENCY_SYMBOLS = {
  usd: '$',
  cad: 'CA$',
  eur: '€',
  gbp: '£',
  aud: 'A$',
  jpy: '¥',
  inr: '₹',
  mxn: 'MX$',
};

// Map of currency codes to their full names
const CURRENCY_NAMES = {
  usd: 'US Dollar',
  cad: 'Canadian Dollar',
  eur: 'Euro',
  gbp: 'British Pound',
  aud: 'Australian Dollar',
  jpy: 'Japanese Yen',
  inr: 'Indian Rupee',
  mxn: 'Mexican Peso',
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'usd', 'cad')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'usd') => {
  const code = currencyCode.toLowerCase();
  return CURRENCY_SYMBOLS[code] || '$';
};

/**
 * Get currency name for a given currency code
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Full currency name
 */
export const getCurrencyName = (currencyCode = 'usd') => {
  const code = currencyCode.toLowerCase();
  return CURRENCY_NAMES[code] || 'US Dollar';
};

/**
 * Format a price with the appropriate currency symbol
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted price string
 */
export const formatPrice = (amount, currencyCode = 'usd', decimals = 2) => {
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = Number(amount).toFixed(decimals);
  return `${symbol}${formattedAmount}`;
};

/**
 * Format price using Intl.NumberFormat for locale-aware formatting
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted price string
 */
export const formatPriceLocale = (amount, currencyCode = 'usd') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
    }).format(amount);
  } catch (e) {
    // Fallback to simple formatting if Intl fails
    return formatPrice(amount, currencyCode);
  }
};
